<template>
  <r-ai-page-container :current-step="7">
    <v-row class="text-start">
      <v-col cols="12">
        <!-- PR-COACH (Block 3-B): mode-choice card. Shown ONLY on first
             landing (controlled by showModeChoice data flag, which the
             user can dismiss by picking either button). Skipped entirely
             when the user has already started reviewing accomplishments,
             so returning users don't see it. -->
        <div v-if="showModeChoice" class="mb-4 mode-choice-wrapper">
          <div class="text-center mb-3">
            <div class="text-h6 font-weight-medium mb-1">
              You're at the most important part
            </div>
            <div class="text-body-2 text--secondary mb-3">
              We'll capture your accomplishments in
              <span class="font-weight-medium">STAR format</span>
              (Situation, Task, Action, Result) &mdash; the structure that turns
              <span style="font-style: italic;">"I led the migration"</span>
              into proof of impact. It's how hiring managers and AI both read a
              resume for capability, not just activity.
            </div>
            <div class="text-body-2 text--secondary">
              Pick how you want to capture them:
            </div>
          </div>
          <v-card outlined class="mb-3 pa-3 mode-card">
            <div class="d-flex align-center mb-2">
              <v-icon color="primary" class="mr-2">mdi-microphone</v-icon>
              <div class="text-body-1 font-weight-medium">
                Talk it through with Sage
              </div>
            </div>
            <div class="text-body-2 text--secondary mb-3">
              Tell Sage your story — by voice or typing — and Sage will
              structure it into STAR. Best if you'd rather narrate than type into forms.
              <span v-if="!modeChoiceUnlocked && modeChoiceStarCap > 0"
                >First {{ modeChoiceStarCap }} wins are on us.</span
              >
            </div>
            <v-btn color="primary" block @click="pickCoachMode">
              Start with Sage
            </v-btn>
          </v-card>
          <v-card outlined class="mb-3 pa-3 mode-card">
            <div class="d-flex align-center mb-2">
              <v-icon color="grey darken-1" class="mr-2">
                mdi-format-list-checks
              </v-icon>
              <div class="text-body-1 font-weight-medium">
                Step-by-step (always free)
              </div>
            </div>
            <div class="text-body-2 text--secondary mb-3">
              Fill in each STAR field yourself, role by role. Best if you want
              full control and prefer to type.
            </div>
            <v-btn
              outlined
              block
              class="ai-outlined-btn"
              @click="pickStepByStep"
            >
              Continue step-by-step
            </v-btn>
          </v-card>
        </div>

        <div
          v-if="
            totalAchievementCount > 0 &&
              !noValues &&
              !milestoneMode &&
              !showModeChoice
          "
          class="mb-4"
        >
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-body-2 font-weight-medium">
              Accomplishment {{ globalAchievementPosition }} of
              {{ totalAchievementCount }}
            </span>
            <span class="text-caption text--secondary">
              {{ completedAchievementCount }} reviewed
            </span>
          </div>
          <v-progress-linear
            :value="progressPercent"
            color="primary"
            height="6"
            rounded
            background-color="grey lighten-3"
          ></v-progress-linear>
        </div>

        <!-- PR-COACH (Block 3-B fix): wrap ALL achievement-review states
             behind !showModeChoice so the mode pick is a true gate. Before
             this, the mode-choice card AND the accomplishment preview both
             rendered simultaneously — overwhelming the user. Now: pick
             first (Sage vs Step-by-step), then the achievement flow appears. -->
        <template v-if="!showModeChoice">
          <div v-if="milestoneMode">
            <div class="milestone-bubble text-center mb-4 pa-6">
              <v-icon size="48" color="amber darken-2" class="mb-3">
                mdi-star-circle
              </v-icon>
              <div class="text-h6 font-weight-medium mb-3">
                {{ milestoneTitle }}
              </div>
              <div class="text-body-1 mb-2">
                {{ milestoneMessage }}
              </div>
              <div class="text-body-2 text--secondary mt-2">
                {{ completedAchievementCount }} of
                {{ totalAchievementCount }} accomplishments reviewed
              </div>
              <v-progress-linear
                :value="progressPercent"
                color="amber darken-2"
                height="8"
                rounded
                background-color="grey lighten-3"
                class="mt-3 mb-4"
              ></v-progress-linear>
            </div>
            <div>
              <v-btn
                color="primary"
                @click="continuePastMilestone()"
                class="my-3"
                block
              >
                Keep going
              </v-btn>
              <v-btn
                class="my-3 ai-outlined-btn"
                outlined
                @click="saveAndExitToDone"
                block
              >
                Save and continue later
              </v-btn>
            </div>
          </div>

          <div v-else-if="overviewMode && currentAchievement">
            <div v-if="!isEditing" class="pb-4 text-body-1">
              {{ textIntro }}
            </div>
            <div class="work-bubble text-body-1 mb-4" v-if="isEditing">
              <div>
                <span class="work-title">Company:</span>
                {{ workGeneralDisplay }}
              </div>
              <div>
                <span class="work-title">Role:</span> {{ workRoleDisplay }}
              </div>
              <div>
                <span class="work-title">Accomplishment:</span>
                {{ currentAchievement.displayCustom }}
              </div>
            </div>
            <r-ai-bubble>
              <div v-if="!isEditing" class="my-2">
                <div
                  style="font-weight: 500;"
                  class="mb-3 text-body-1 text--primary"
                >
                  "{{ currentAchievement.displayCustom }}"
                </div>
                <div class="text-body-2">
                  {{ textFollowup }}
                </div>
              </div>
              <div v-if="isEditing" class="text-body-1 text--primary my-2">
                <div v-if="editSituation">
                  Let's start with the <b>Situation</b>. What was the context or
                  background that made this action necessary?
                </div>
                <div v-if="editTask">
                  Perfect! Now for the <b>Task</b>. What was your specific
                  responsibility or what where you asked to do?
                </div>
                <div v-if="editAction">
                  Great! Now the <b>Action</b>. What specific steps did you
                  take?
                </div>
                <div v-if="editResult">
                  Finally the <b>Result</b>. What was the outcome of your
                  actions?
                </div>
              </div>

              <div v-if="isEditing" class="text-body-1 mt-2">
                <voice-input
                  v-if="editSituation"
                  v-model="situation"
                  label="Situation"
                  placeholder="Describe the context or background..."
                  hint="Tap the mic to speak, or type your answer"
                  :multiline="true"
                  :rows="3"
                ></voice-input>
                <voice-input
                  v-if="editTask"
                  v-model="task"
                  label="Task"
                  placeholder="What was your specific responsibility..."
                  hint="Tap the mic to speak, or type your answer"
                  :multiline="true"
                  :rows="3"
                ></voice-input>
                <voice-input
                  v-if="editAction"
                  v-model="action"
                  label="Action"
                  placeholder="What specific steps did you take..."
                  hint="Tap the mic to speak, or type your answer"
                  :multiline="true"
                  :rows="3"
                ></voice-input>
                <voice-input
                  v-if="editResult"
                  v-model="result"
                  label="Result"
                  placeholder="What was the outcome of your actions..."
                  hint="Tap the mic to speak, or type your answer"
                  :multiline="true"
                  :rows="3"
                ></voice-input>
              </div>
            </r-ai-bubble>
            <div class="pt-2" v-if="isEditing">
              <v-btn class="my-4" color="primary" @click="nextEdit()" block>
                Continue
              </v-btn>
              <v-btn
                class="my-4 ai-outlined-btn"
                outlined
                @click="saveAndExitToDone"
                block
              >
                Save and continue later
              </v-btn>
            </div>
            <div class="pt-2" v-else>
              <v-btn class="my-4" color="primary" @click="nextEdit()" block>
                Let's dive deeper
              </v-btn>
              <v-btn
                class="my-4 ai-outlined-btn"
                outlined
                @click="nextAchievement()"
                block
              >
                Skip this one
              </v-btn>
              <v-btn
                class="my-4 ai-outlined-btn"
                outlined
                @click="saveAndExitToDone"
                block
              >
                Save and continue later
              </v-btn>
            </div>
          </div>
          <div v-else-if="storyMode">
            <div class="my-2 text-body-1 text--primary">
              Perfect! Here's your consolidated story based on what we
              discussed:
            </div>
            <div class="work-bubble text-body-1 mb-2" v-if="isEditing">
              <div class="work-title">
                Situation:
              </div>
              <div>{{ situation }}</div>
              <div class="work-title pt-3">Task:</div>
              <div>{{ task }}</div>
              <div class="work-title pt-3">Action:</div>
              <div>{{ action }}</div>
              <div class="work-title pt-3">Result:</div>
              <div>{{ result }}</div>
            </div>
            <r-ai-bubble class="mb-2">
              <div class="my-2 text-body-1 text--primary">
                Does this consolidated story look right? Does it accurately
                capture your accomplishment?
              </div>
            </r-ai-bubble>
            <div class="">
              <v-btn
                class="my-4"
                color="primary"
                @click="storyLooksGood()"
                block
              >
                Yes, looks good.
              </v-btn>
              <v-btn
                class="my-4 ai-outlined-btn"
                outlined
                @click="editStory()"
                block
              >
                No, let me edit.
              </v-btn>
              <v-btn
                class="my-4 ai-outlined-btn"
                outlined
                @click="saveAndExitToDone"
                block
              >
                Save and continue later
              </v-btn>
            </div>
          </div>
          <div v-else-if="suggestionMode">
            <!-- PR-AGENT-UX: explicit framing that this is a polish-pass on the
               user's own words, not an AI-written story. Carlos's principle:
               we want the user's authentic voice. The AI rephrases what they
               typed; it does not invent content. -->
            <div class="mb-2 text-body-1 text--primary">
              Here's a rephrased version of what you wrote — your story, just
              tightened up:
            </div>
            <div
              class="text-caption text--secondary mb-3"
              style="font-style: italic;"
            >
              We recommend editing this to make sure it still sounds like you.
              Your authentic voice always beats AI polish.
            </div>
            <div class="d-flex align-center mt-1 mb-3 flex-wrap">
              <span
                class="text-caption text--secondary mr-3"
                style="white-space: nowrap;"
                >Length:</span
              >
              <v-btn-toggle
                v-model="lengthIndex"
                mandatory
                dense
                class="length-toggle"
                @change="onLengthChange"
              >
                <v-btn x-small outlined>Concise</v-btn>
                <v-btn x-small outlined>Medium</v-btn>
                <v-btn x-small outlined>Detailed</v-btn>
              </v-btn-toggle>
            </div>
            <div class="green-bubble text-body-1 mb-2">
              <div class="green-title">Suggested summary:</div>
              <div v-if="loadingSuggestedSummary">
                <v-progress-circular
                  indeterminate
                  color="#016630"
                  size="24"
                  class="mr-3"
                ></v-progress-circular>
                Loading...
              </div>
              <div v-if="summaryError" class="error--text">
                {{ summaryError }}
              </div>
              <div v-else-if="!editingSuggestion">{{ suggestedSummary }}</div>
              <div v-else-if="editingSuggestion">
                <v-textarea
                  v-model="suggestedSummary"
                  auto-grow
                  outlined
                  dense
                ></v-textarea>
                <v-btn @click="doneEditSuggestion()" outlined>
                  Done Editing
                </v-btn>
              </div>
            </div>
            <r-ai-bubble class="text-body-1 mb-2">
              <ai-line
                title="Original summary"
                :text="originalSummary"
              ></ai-line>
            </r-ai-bubble>
            <div class="">
              <v-btn
                class="my-4"
                color="primary"
                @click="saveSuggestion()"
                :disabled="
                  editingSuggestion ||
                    errorGettingSummary ||
                    loadingSuggestedSummary
                "
                block
              >
                Use the suggested summary
              </v-btn>
              <v-btn
                class="my-4 ai-outlined-btn"
                outlined
                @click="editSuggestion()"
                :disabled="
                  editingSuggestion ||
                    errorGettingSummary ||
                    loadingSuggestedSummary
                "
                block
              >
                Edit the suggestion
              </v-btn>
              <v-btn
                class="my-4 ai-outlined-btn"
                outlined
                @click="useOriginal()"
                block
              >
                Discard suggestion and use original
              </v-btn>
              <v-btn
                class="my-4 ai-outlined-btn"
                outlined
                @click="saveAndExitToDone"
                block
              >
                Save and continue later
              </v-btn>
            </div>
          </div>
          <div v-else-if="completionMode">
            <div class="text-body-1 text--primary mb-2">
              Great job! You've improved this accomplishment by adding more
              detail and context.
            </div>
            <r-ai-bubble class="mb-2 text-body-1 text--primary">
              <div class="my-2 text-body-1 text--primary">
                {{ completionModeMessage }}
              </div>
            </r-ai-bubble>
            <div class="">
              <v-btn
                color="primary"
                @click="exitCompletionMode()"
                class="my-4"
                block
              >
                {{
                  hasMoreAchievements
                    ? "Continue with the next accomplishment"
                    : "Finish up"
                }}
              </v-btn>
              <v-btn
                class="my-4 ai-outlined-btn"
                outlined
                @click="saveAndExitToDone"
                block
              >
                Save and continue later
              </v-btn>
            </div>
          </div>
          <div v-else-if="noValues || !currentAchievement">
            <div class="pb-4 text-body-1">
              {{ textIntro }}
            </div>
            <r-ai-bubble class="mb-2 text-body-1 text--primary">
              <div class="my-2">
                We didn't find any work accomplishments to review in your
                resume. You can add more accomplishments later using the profile
                editor.
              </div>
            </r-ai-bubble>
            <div class="pt-2">
              <v-btn
                class="my-4"
                color="primary"
                @click="saveAndExitToDone"
                block
              >
                Continue
              </v-btn>
            </div>
          </div>
        </template>
      </v-col>
    </v-row>
  </r-ai-page-container>
</template>

<script>
import { mapFields } from "vuex-map-fields";
import { mapGetters, mapActions, mapState } from "vuex";
import * as builder from "../../store/modules/work_builder";
import * as resApi from "../../api/ResumeApi";
import AILine from "../../components/AI/AILine.vue";
import VoiceInput from "../../components/VoiceInput.vue";

export default {
  name: "AIWorkAchievements",
  components: { "ai-line": AILine, "voice-input": VoiceInput },
  data: () => ({
    isEditing: false,
    editSituation: false,
    editTask: false,
    editAction: false,
    editResult: false,
    overviewMode: true,
    storyMode: false,
    suggestionMode: false,
    completionMode: false,
    milestoneMode: false,
    originalSummary: "",
    loadingSuggestedSummary: true,
    editingSuggestion: false,
    suggestedSummary: "",
    summaryError: "",
    errorGettingSummary: false,
    lengthIndex: 1,
    lengthLabels: ["Concise", "Medium", "Detailed"],
    lengthValues: ["short", "medium", "long"],
    keyIndex: 0,
    achievementIndex: 0,
    noValues: false,
    currentAchievement: null,
    situation: "",
    task: "",
    action: "",
    result: "",
    completedAchievementCount: 0,
    totalAchievementCount: 0,
    lastMilestoneAt: 0,
    pendingAdvance: false,
    // PR-COACH (Block 3-B): mode-choice card state. Shown on landing
    // unless the user already has reviewed achievements OR has
    // explicitly dismissed (the route ?mode=step query bypasses).
    showModeChoice: false,
    modeChoiceUnlocked: false,
    modeChoiceStarCap: 3
  }),
  methods: {
    // PR-COACH (Block 3-B): mode-choice handlers.
    async loadCoachEntitlement() {
      try {
        // Best-effort — if the call fails, the card still renders with
        // the defaults (cap=3, unlocked=false). Lazy import the API
        // client so the existing AIWorkAchievements page doesn't pull
        // SageOnboardingApi in at top level.
        const { getOnboardingStatus } = await import(
          "../../api/SageOnboardingApi"
        );
        const resp = await getOnboardingStatus();
        if (resp && resp.success && resp.capState) {
          this.modeChoiceUnlocked = !!resp.capState.unlimited;
          this.modeChoiceStarCap = resp.capState.starCap || 3;
        }
      } catch (e) {
        // Silent — card renders with defaults
      }
    },
    pickCoachMode() {
      // Slide to the Coach screen with the current work key pre-selected
      // so Sage starts on the same role the step-by-step flow would.
      this.showModeChoice = false;
      const currentKey =
        this.fullList && this.fullList[this.keyIndex]
          ? this.fullList[this.keyIndex].key
          : null;
      this.$router.push({
        path: "/AIWorkAchievementsCoach",
        query: currentKey ? { workKey: currentKey } : {}
      });
    },
    pickStepByStep() {
      // Stay on this page; just dismiss the card. The existing
      // beforeMount() already set up the first achievement.
      this.showModeChoice = false;
    },
    // PR-AGENT-UX: Save STAR fields before exiting to /AIDone. The previous
    // implementation used `href="/AIDone"` which is a raw browser navigation
    // that fires BEFORE any save action — so STAR fields the user typed
    // were silently lost when they clicked "Save and continue later".
    // Now we save first, then navigate via the router.
    async saveAndExitToDone() {
      try {
        await this.saveWork();
      } catch (e) {
        // If the save fails, still let the user out — but log it so we
        // can fix any save bugs downstream. Don't trap them in this page.
        console.warn("saveAndExitToDone failed:", e);
      }
      this.$router.push({ path: "/AIDone" });
    },
    computeTotalAchievements() {
      let total = 0;
      if (this.fullList) {
        for (let i = 0; i < this.fullList.length; i++) {
          const work = this.fullList[i];
          const achList = work.achievements || work.achievementList || [];
          if (achList.length > 0) {
            total += achList.length;
          }
        }
      }
      this.totalAchievementCount = total;
    },
    // PR-COACH-PROGRESS-RECALIBRATE: seed completedAchievementCount from the
    // ACTUAL STAR completion across the whole queue, not 0. Before this, the
    // "X reviewed" counter + progress bar were session-local: a user who had
    // already completed several STARs (e.g. in Sage) and then switched to
    // step-by-step mid-flow saw "0 reviewed" and a near-empty bar, because the
    // counter only tracked actions taken in THIS session. We count every
    // achievement whose four BRAM fields are all non-empty (the shared
    // isStarComplete rule the Sage Coach uses too) so the bar reflects real
    // progress the moment the page loads. This only sets the BASELINE — the
    // existing per-review increments build on top of it, and the landing
    // logic (resolveLandingPosition / keyIndex / achievementIndex) is
    // untouched, so where the user lands is unchanged.
    computeCompletedFromActual() {
      let done = 0;
      if (this.fullList) {
        for (let i = 0; i < this.fullList.length; i++) {
          const work = this.fullList[i];
          const achList = work.achievementList || work.achievements || [];
          for (let j = 0; j < achList.length; j++) {
            if (this.isStarComplete(achList[j])) {
              done += 1;
            }
          }
        }
      }
      this.completedAchievementCount = done;
      // Keep the milestone tracker in step with the seeded baseline so a
      // milestone the user already passed in a prior session/Sage flow does
      // not re-fire the instant they review one more.
      this.lastMilestoneAt = done;
    },
    // PR-COACH-RESUME-FIX-2: the ONE shared definition of "STAR complete" —
    // all four BRAM fields (Background/Responsibility/ActionResult/Measurement,
    // i.e. Situation/Task/Action/Result) non-empty after trim. The Sage Coach's
    // buildAccomplishmentQueue uses the identical rule, so completion can never
    // disagree between the two views. Note: this counts the SAVED STAR detail,
    // not the free-cap usage counter (which increments on any approve and so
    // could read "3 captured" even when a win only had 3 of 4 fields).
    isStarComplete(a) {
      return !!(
        a &&
        ("" + (a.background || "")).trim() &&
        ("" + (a.responsibility || "")).trim() &&
        ("" + (a.actionResult || "")).trim() &&
        ("" + (a.measurement || "")).trim()
      );
    },
    // PR-COACH-RESUME-FIX-2: resolve where the step-by-step walkthrough should
    // open as a single { keyIndex, achievementIndex } across the whole
    // fullList. Honors a handoff hint when it points at an achievement that
    // still needs work; otherwise lands on the global first-incomplete; final
    // fallback is the hint (or 0,0) so we never return a position that doesn't
    // exist. This keeps the existing stale-pointer fallbacks intact.
    resolveLandingPosition(wantKey, wantAchievementId) {
      const list = this.fullList || [];
      const achListOf = function(w) {
        return (w && (w.achievementList || w.achievements)) || [];
      };

      // 1. Exact hint, only if it still needs work.
      if (wantKey) {
        const ki = list.findIndex(function(w) {
          return w && w.key === wantKey;
        });
        if (ki >= 0) {
          const achList = achListOf(list[ki]);
          if (wantAchievementId) {
            const ai = achList.findIndex(function(a) {
              return a && a.id === wantAchievementId;
            });
            if (ai >= 0 && !this.isStarComplete(achList[ai])) {
              return { keyIndex: ki, achievementIndex: ai };
            }
          }
        }
      }

      // 2. Global first-incomplete across every work entry.
      for (let ki = 0; ki < list.length; ki++) {
        const achList = achListOf(list[ki]);
        for (let ai = 0; ai < achList.length; ai++) {
          if (!this.isStarComplete(achList[ai])) {
            return { keyIndex: ki, achievementIndex: ai };
          }
        }
      }

      // 3. Everything complete (or no achievements): fall back to the hinted
      //    slot if it resolves, else the very first position.
      if (wantKey) {
        const ki = list.findIndex(function(w) {
          return w && w.key === wantKey;
        });
        if (ki >= 0) {
          const achList = achListOf(list[ki]);
          let ai = 0;
          if (wantAchievementId) {
            const found = achList.findIndex(function(a) {
              return a && a.id === wantAchievementId;
            });
            if (found >= 0) ai = found;
          }
          return { keyIndex: ki, achievementIndex: ai };
        }
      }
      return { keyIndex: 0, achievementIndex: 0 };
    },
    getMilestone(count) {
      if (count === 2) {
        return {
          title: "Great start!",
          message:
            "You've got enough for a basic JD Match. Keep going \u2014 the next few unlock even more."
        };
      } else if (count === 4) {
        return {
          title: "Building momentum!",
          message:
            "Sage can now write a solid Career Story from your evidence. Try it in Career Tools when you're done."
        };
      } else if (count === 6) {
        return {
          title: "Nice progress!",
          message:
            "Your profile supports Skills Gap Analysis now. Sage can spot what's missing and help you fill it."
        };
      } else if (count === 8) {
        return {
          title: "Really cooking!",
          message:
            "You've got enough depth for Interview Prep. Sage can generate practice questions from your real experience."
        };
      } else if (count === 10) {
        return {
          title: "Double digits!",
          message:
            "Targeted Resume matching is now highly accurate. Paste any job description and get a tailored resume."
        };
      } else if (count === 12) {
        return {
          title: "Impressive depth!",
          message:
            "Your Period Summary will be detailed and powerful \u2014 great for performance reviews."
        };
      } else if (count === 14) {
        return {
          title: "Outstanding!",
          message:
            "Very few professionals document this thoroughly. Every tool is operating at full accuracy."
        };
      } else if (count === 16) {
        return {
          title: "Remarkable!",
          message:
            "You're setting the gold standard. Your career evidence is truly comprehensive."
        };
      } else if (count === 18) {
        return {
          title: "Incredible!",
          message:
            "This profile would impress any hiring manager or review panel."
        };
      } else if (count === 20) {
        return {
          title: "Career evidence champion!",
          message:
            "20 achievements in one session \u2014 that's extraordinary. Your profile is best-in-class."
        };
      }
      return null;
    },
    checkMilestone() {
      if (
        this.completedAchievementCount > 0 &&
        this.completedAchievementCount % 2 === 0 &&
        this.completedAchievementCount <= 20 &&
        this.completedAchievementCount !== this.lastMilestoneAt &&
        this.hasMoreAchievements
      ) {
        this.lastMilestoneAt = this.completedAchievementCount;
        this.milestoneMode = true;
        this.overviewMode = false;
        this.storyMode = false;
        this.suggestionMode = false;
        this.completionMode = false;
        return true;
      }
      return false;
    },
    continuePastMilestone() {
      this.milestoneMode = false;
      this.overviewMode = true;
    },
    findNextWorkWithAchievements() {
      while (this.keyIndex + 1 < this.fullList.length) {
        this.keyIndex += 1;
        this.key = this.fullList[this.keyIndex].key;

        if (this.achievementList && this.achievementList.length > 0) {
          this.achievementIndex = 0;
          this.currentAchievement = this.achievementList[0];
          return true;
        }
      }
      this.noValues = true;
      return false;
    },
    nextAchievement() {
      this.completedAchievementCount += 1;

      if (this.checkMilestone()) {
        this.pendingAdvance = true;
        return;
      }

      this.advanceToNextAchievement();
    },
    advanceToNextAchievement() {
      this.lengthIndex = 1;
      if (
        this.achievementList &&
        this.achievementList.length > 0 &&
        this.achievementIndex + 1 < this.achievementList.length
      ) {
        this.achievementIndex += 1;
        this.currentAchievement = this.achievementList[this.achievementIndex];
        this.isEditing = false;
      } else {
        if (this.keyIndex + 1 < this.fullList.length) {
          if (this.findNextWorkWithAchievements()) {
            this.isEditing = false;
          } else {
            this.$router.push({ path: "/AIDone" });
          }
        } else {
          this.$router.push({ path: "/AIDone" });
        }
      }
    },
    nextEdit() {
      this.isEditing = true;
      if (this.editSituation == true) {
        this.editSituation = false;
        this.editTask = true;
      } else if (this.editTask == true) {
        this.editTask = false;
        this.editAction = true;
      } else if (this.editAction == true) {
        this.editAction = false;
        this.editResult = true;
      } else if (this.editResult == true) {
        this.editResult = false;
        this.overviewMode = false;
        this.completionMode = false;
        this.suggestionMode = false;
        this.storyMode = true;
        this.originalSummary = this.currentAchievement.displayCustom;
      } else {
        this.situation = this.currentAchievement.background;
        this.task = this.currentAchievement.responsibility;
        this.action = this.currentAchievement.actionResult;
        this.result = this.currentAchievement.measurement;
        this.editSituation = true;
        this.summaryError = "";
        this.errorGettingSummary = false;
      }
    },
    useOriginal() {
      this.saveWork();
      this.completedAchievementCount += 1;

      if (this.checkMilestone()) {
        this.pendingAdvance = true;
        this.overviewMode = false;
        this.storyMode = false;
        this.suggestionMode = false;
        this.completionMode = false;
        return;
      }

      this.advanceToNextAchievement();
      this.overviewMode = true;
      this.storyMode = false;
      this.suggestionMode = false;
      this.completionMode = false;
    },
    saveSuggestion() {
      this.currentAchievement.displayCustom = this.suggestedSummary;
      this.achievementList[this.achievementIndex] = this.currentAchievement;
      this.saveWork();

      this.overviewMode = false;
      this.storyMode = false;
      this.suggestionMode = false;
      this.completionMode = true;
    },
    editStory() {
      this.overviewMode = true;
      this.storyMode = false;
      this.isEditing = true;
      this.editAction = false;
      this.editTask = false;
      this.editResult = false;
      this.editSituation = true;
    },
    async storyLooksGood() {
      this.currentAchievement.background = this.situation;
      this.currentAchievement.responsibility = this.task;
      this.currentAchievement.actionResult = this.action;
      this.currentAchievement.measurement = this.result;
      this.achievementList[this.achievementIndex] = this.currentAchievement;
      await this.saveWork();
      this.buildAIPhrasing();

      this.overviewMode = false;
      this.storyMode = false;
      this.suggestionMode = true;
    },
    exitCompletionMode() {
      this.storyMode = false;
      this.suggestionMode = false;
      this.completionMode = false;
      this.overviewMode = true;

      this.completedAchievementCount += 1;

      if (this.checkMilestone()) {
        this.pendingAdvance = true;
        return;
      }

      this.advanceToNextAchievement();
    },
    onLengthChange() {
      if (this.suggestionMode && !this.loadingSuggestedSummary) {
        this.buildAIPhrasing();
      }
    },
    async buildAIPhrasing() {
      try {
        this.loadingSuggestedSummary = true;
        this.editingSuggestion = false;
        const phrasing = `${this.situation} ${this.task} ${this.action} ${this.result}`;
        let result = await resApi.FetchAiOptimizedPhrasing({
          resumeId: this.$store.state.resumeId,
          phrasing: phrasing,
          context: "work-accomplishment",
          conciseness: this.lengthValues[this.lengthIndex]
        });

        if (!result.success) {
          console.error("Failed to get AI suggested summary");
          this.summaryError =
            "We were unable to generate a suggestion at this time.";
          this.errorGettingSummary = true;
          return;
        } else {
          this.summaryError = "";
          this.errorGettingSummary = false;
          this.suggestedSummary = result.data.optimizedPhrasing;
        }
      } catch (error) {
        console.error("Error building AI phrasing:", error);
      } finally {
        this.loadingSuggestedSummary = false;
      }
    },
    editSuggestion() {
      this.editingSuggestion = true;
    },
    doneEditSuggestion() {
      this.editingSuggestion = false;
    },
    edit() {
      this.isEditing = true;
    },
    save() {
      this.saveWork();
      this.next();
    },
    ...mapActions("work", ["saveWork"])
  },
  computed: {
    ...mapGetters("work", ["fullList", "doesKeyExist", "baseDegrees"]),
    ...mapState({
      // PR-COACH-POINTER: where the user left off in the Coach flow.
      // Step-by-step honors this on mount so a free user who hits the
      // 3-STAR cap and switches to "Continue manually" lands on the SAME
      // accomplishment they were working — not back at job 1, achievement 1.
      // Survives Stripe roundtrip + reload via vuex-persistedstate.
      coachPointer: state => state.work && state.work.coachPointer
    }),
    ...mapFields("work", {
      key: "key",
      display: "workGeneral.display",
      companyName: "workGeneral.companyName",
      companyCity: "workGeneral.companyCity",
      companyState: "workGeneral.companyState",
      companyCountry: "workGeneral.companyCountry",
      industry: "workGeneral.industry",
      workDict: "workDict",
      startDate: "workGeneral.startDate",
      endDate: "workGeneral.endDate",
      positions: "workRole.positions",
      description: "workResponsibilities.description",
      workingCurrently: "workGeneral.workingCurrently",
      workGeneral: "workGeneral",
      workRole: "workRole",
      achievementList: "achievementList"
    }),
    position: {
      get() {
        if (this.positions && this.positions.length > 0) {
          return this.positions[0].title;
        }
        return "";
      },
      set(newvalue) {
        if (this.positions && this.positions.length > 0) {
          this.positions[0].title = newvalue;
        } else {
          this.positions = [{ title: newvalue, role: "", key: "1" }];
        }
      }
    },
    globalAchievementPosition() {
      let position = 0;
      if (this.fullList) {
        for (let i = 0; i < this.keyIndex; i++) {
          const work = this.fullList[i];
          const achList = work.achievements || work.achievementList || [];
          if (achList.length > 0) {
            position += achList.length;
          }
        }
      }
      position += this.achievementIndex + 1;
      return position;
    },
    progressPercent() {
      if (this.totalAchievementCount === 0) return 0;
      return Math.round(
        (this.completedAchievementCount / this.totalAchievementCount) * 100
      );
    },
    hasMoreAchievements() {
      if (
        this.achievementList &&
        this.achievementList.length > 0 &&
        this.achievementIndex + 1 < this.achievementList.length
      ) {
        return true;
      }
      if (this.fullList) {
        for (let i = this.keyIndex + 1; i < this.fullList.length; i++) {
          const work = this.fullList[i];
          if (work.achievementList && work.achievementList.length > 0) {
            return true;
          }
        }
      }
      return false;
    },
    milestoneTitle() {
      const milestone = this.getMilestone(this.completedAchievementCount);
      return milestone ? milestone.title : "Great work!";
    },
    milestoneMessage() {
      const milestone = this.getMilestone(this.completedAchievementCount);
      return milestone
        ? milestone.message
        : "You're making great progress on your Career Profile.";
    },
    completionModeMessage() {
      if (this.hasMoreAchievements) {
        return "You have more accomplishments we could work on. Would you like to keep going, or save and come back later?";
      }
      return "That was your last accomplishment \u2014 nice work! Your Career Profile is ready to go.";
    },
    editFieldLabel() {
      if (this.editSituation) {
        return "Describe the situation:";
      } else if (this.editTask) {
        return "Describe your task or responsibility:";
      } else if (this.editAction) {
        return "Describe the actions you took:";
      } else if (this.editResult) {
        return "Describe the results and outcomes:";
      } else {
        return "";
      }
    },
    achievementCount() {
      return this.achievementList?.length ?? 0;
    },
    textIntro() {
      if (this.noValues) {
        return "We didn't find any work accomplishment entries in your resume for this work. You can add more in the full resumation tool.";
      } else if (this.keyIndex === 0 && this.achievementIndex === 0) {
        return `We identified accomplishments tied to your work records. Here's what I extracted from your resume from your time at ${this.workGeneralDisplay}:`;
      } else {
        return `Here's the next accomplishment I extracted from your time at ${this.workGeneralDisplay}:`;
      }
    },
    textFollowup() {
      if (this.noValues) {
        return "";
      } else {
        return "Let's go a little deeper on this to understand more about the complete story.";
      }
    },
    workRoleDisplay() {
      if (this.noValues) {
        return "";
      }
      return builder.buildRolesDisplay(this.workRole).join(", ");
    },
    workDateDisplay() {
      if (this.noValues) {
        return "";
      }
      return builder.buildDateDisplay(this.workGeneral);
    },
    workLocationDisplay() {
      if (this.noValues) {
        return "";
      }
      return builder.buildLocationDisplay(this.workGeneral);
    },
    workGeneralDisplay() {
      if (this.noValues) {
        return "";
      }
      return builder.buildGeneralDisplay(this.workGeneral);
    }
  },
  watch: {
    milestoneMode(newVal) {
      if (!newVal && this.pendingAdvance) {
        this.pendingAdvance = false;
        this.advanceToNextAchievement();
      }
    }
  },
  beforeMount() {
    if (this.fullList && this.fullList.length > 0) {
      this.computeTotalAchievements();
      // PR-COACH-PROGRESS-RECALIBRATE: seed the reviewed counter + progress
      // bar from real STAR completion so a user who finished STARs elsewhere
      // (e.g. in Sage) sees accurate progress, not "0 reviewed", on landing.
      this.computeCompletedFromActual();

      // PR-FIX-COACH-HANDOFF: honor a ?workKey query so arriving here from
      // the Coach's "switch to step-by-step" resumes on the SAME role the
      // user was working, instead of snapping back to the first job.
      //
      // PR-COACH-POINTER: resolution order for "where should we land":
      //   1. ?workKey + ?achievementId from query — direct handoff from
      //      Coach's switchToStepByStep (most authoritative; the user just
      //      clicked the button so this reflects their intent right now).
      //   2. Vuex coachPointer — preserved across Stripe roundtrip, reload,
      //      and back-button navigation. Set continuously by the Coach
      //      watcher so it always reflects the latest position.
      //   3. Default to first work entry, first achievement.
      // Stale-pointer guards: if any reference doesn't resolve, fall through
      // to the next tier rather than crashing.
      var wantKey = this.$route.query && this.$route.query.workKey;
      var wantAchievementId =
        this.$route.query && this.$route.query.achievementId;

      // Tier 2: fall back to Vuex pointer if no query handoff
      if (!wantKey && this.coachPointer && this.coachPointer.workKey) {
        wantKey = this.coachPointer.workKey;
        wantAchievementId = this.coachPointer.achievementId;
      }

      // PR-COACH-RESUME-FIX-2: resolve a SINGLE (keyIndex, achievementIndex)
      // position and apply all three of keyIndex / achievementIndex /
      // currentAchievement together. The previous code set currentAchievement
      // but left achievementIndex at its default 0 — so the position counter
      // ("Accomplishment N of M") and the next/save navigation walked from the
      // first achievement of the work entry, not the one actually shown.
      //
      // Resolution rules (uses the shared all-4-BRAM completeness definition so
      // the Coach and step-by-step never disagree on "done"):
      //   1. If the handoff/pointer targets an achievement that still needs
      //      work, land EXACTLY there (honors the user's just-made choice).
      //   2. Otherwise (no hint, hint not found, or hint points at a STAR that
      //      is already complete — e.g. the cap-fork win), land on the GLOBAL
      //      first-incomplete achievement across every work entry, not just the
      //      hinted entry. This is what makes "switch to step-by-step" / a
      //      post-Stripe return land on the next gap rather than index 0 or a
      //      finished win.
      //   3. If everything is complete, fall back to the hinted slot (or the
      //      very first achievement) so we never strand on an empty position.
      var pos = this.resolveLandingPosition(wantKey, wantAchievementId);
      this.keyIndex = pos.keyIndex;
      this.key = this.fullList[pos.keyIndex].key;

      if (this.achievementList && this.achievementList.length > 0) {
        var landIdx =
          pos.achievementIndex >= 0 &&
          pos.achievementIndex < this.achievementList.length
            ? pos.achievementIndex
            : 0;
        this.achievementIndex = landIdx;
        this.currentAchievement = this.achievementList[landIdx];
      } else {
        this.findNextWorkWithAchievements();
      }
    } else {
      this.noValues = true;
    }
  },
  mounted() {
    // PR-COACH (Block 3-B): decide whether to show the mode-choice card.
    // Skip when:
    //   • The user has already reviewed accomplishments in this flow
    //     (returning visit — don't re-pitch the choice).
    //   • The route was reached with ?mode=step (explicit step-by-step
    //     pick from elsewhere — e.g. coach declined to upgrade).
    //   • There's nothing to review (noValues).
    var explicitMode = this.$route.query && this.$route.query.mode;
    var hasProgress = this.completedAchievementCount > 0;
    if (explicitMode === "step" || hasProgress || this.noValues) {
      this.showModeChoice = false;
      return;
    }
    this.showModeChoice = true;
    this.loadCoachEntitlement();
  }
};
</script>

<style scoped>
.work-bubble {
  background-color: #eff6ff;
  border-radius: 10px;
  padding: 16px;
  color: #000000;
  border-color: #bedbff;
  border-width: 2px;
  border-style: solid;
}

.work-title {
  font-weight: 500;
  color: #193c8b;
}

.edit-bubble {
  background-color: #f9fafb;
  border-radius: 10px;
  padding: 16px;
}

.edit-title {
  font-weight: 500;
  color: #4a5565;
}

.add-bubble {
  background-color: #eff6ff;
  border-radius: 10px;
  padding: 16px;
}

.green-bubble {
  background-color: #f0fdf4;
  border-radius: 10px;
  padding: 16px;
  border-color: #b9f8cf;
  border-width: 2px;
  border-style: solid;
}

.green-title {
  font-weight: 500;
  color: #016630;
}

.milestone-bubble {
  background: linear-gradient(135deg, #fff8e1 0%, #fff3c4 50%, #ffe082 100%);
  border-radius: 16px;
  border: 2px solid #ffca28;
}
</style>

<style>
.theme--dark .work-bubble {
  background-color: #1a2744;
  color: #e0e8f0;
  border-color: #2e4a7a;
}
.theme--dark .work-title {
  color: #7eb3ff;
}
.theme--dark .edit-bubble {
  background-color: #2d2d2d;
}
.theme--dark .edit-title {
  color: #b0bec5;
}
.theme--dark .add-bubble {
  background-color: #1a2744;
}
.theme--dark .green-bubble {
  background-color: #0d2818;
  border-color: #1b5e32;
  color: #c8e6c9;
}
.theme--dark .green-title {
  color: #66bb6a;
}
.theme--dark .milestone-bubble {
  background: linear-gradient(135deg, #3e2723 0%, #4e342e 50%, #5d4037 100%);
  border-color: #ff8f00;
  color: #ffe082;
}
.length-toggle .v-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-size: 0.75rem !important;
  min-width: 60px !important;
}
</style>
